import React, { useState, useContext, useEffect, useRef } from "react";
import {
  LoadingOverlay,
  Text,
  Button,
  Group as MantineGroup,
  Stack,
  Center,
  ThemeIcon,
  Box,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import FormField from "./form-field";
import { DataContext } from "../context/DataContext";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import useAdjustInputSize from "../hooks/useAdjustInputSize";

const BasicDataCollectionForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  reAttempts,
  onApiResponse,
}) => {
  const { data, text, dispatch, settings } = useContext(DataContext);
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(reAttempts);
  const [statusResponse, setStatusResponse] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputSize = useAdjustInputSize();
  const lastConfirmationRef = useRef(null);
  const confirmationsReceivedRef = useRef([]);
  const totalAttemptsUsedRef = useRef(0);
  const waitingForConfirmationChangeRef = useRef(false);
  const initialLoadRef = useRef(true);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasShownFormForCurrentAttempt, setHasShownFormForCurrentAttempt] = useState(false);

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  // Status check handler
  const handleStatusResult = (statusResult, confirmationChanged) => {
    if (initialLoadRef.current && statusResult) {
      initialLoadRef.current = false;
    }

    setStatusResponse(statusResult);
    const confirmationNumber = statusResult.confirmation || "unknown";

    if (statusResult.status === "PASS") {
      try {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      } catch (err) {
        // Silent fail
      }
      setLoading(true);
      return true;
    }

    if (
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt &&
      initialLoadRef.current
    ) {
      initialLoadRef.current = false;
      setCurrentAttempt(1);
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setResult(false);
      return false;
    }

    if (
      (currentAttempt === 0 || currentAttempt === 1) &&
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt
    ) {
      lastConfirmationRef.current = confirmationNumber;
      if (currentAttempt === 0) {
        setCurrentAttempt(1);
      }
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setResult(false);
      return false;
    }

    if (waitingForConfirmationChangeRef.current && confirmationChanged) {
      waitingForConfirmationChangeRef.current = false;
      lastConfirmationRef.current = confirmationNumber;
      confirmationsReceivedRef.current.push(confirmationNumber);
      setHasShownFormForCurrentAttempt(false);

      if (statusResult.status !== "PASS" && attemptsLeft > 0) {
        setLoading(false);
        setResult(false);
        return false;
      }

      if (statusResult.status !== "PASS" && attemptsLeft === 0) {
        try {
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        } catch (err) {
          // Silent fail
        }
      }

      setLoading(true);
      return true;
    }

    try {
      dispatch({ type: "SET_STATUS", payload: "PENDING" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
    } catch (err) {
      // Silent fail
    }

    setLoading(true);

    if (!result) {
      setResult(true);
    }

    return true;
  };

  // Status check hooks
  useStatusCheck(result, false, componentKey, handleStatusResult);

  // Form setup
  const form = useForm({
    initialValues: config.fields.reduce((acc, field) => {
      if (field.type === "address") {
        acc["addr"] = data.addr || "";
        acc["city"] = data.city || "";
        acc["state"] = data.state || "";
        acc["zip"] = data.zip || "";
      } else {
        acc[field.name] = data[field.name] || field.initialValue || "";
      }
      return acc;
    }, {}),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};
      config.fields.forEach((field) => {
        if (field.validation) {
          let error;
          if (field.name === "address" && field.type === "address") {
            error = field.validation({
              addr: values.addr || "",
              city: values.city || "",
              state: values.state || "",
              zip: values.zip || "",
            });
          } else {
            error = field.validation(values[field.name]);
          }
          if (error) {
            if (field.type === "address") {
              errors.addr = error;
            } else {
              errors[field.name] = error;
            }
          }
        }
      });
      return errors;
    },
  });

  // Submit handler
  const submitHandler = async () => {
    setIsVerifying(true);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    const newAttemptsLeft = attemptsLeft - 1;
    setAttemptsLeft(newAttemptsLeft);

    Object.keys(form.values).forEach((key) => {
      data[key] = form.values[key];
    });

    if (!data.settings) data.settings = {};
    data.settings.currentAttempt = currentAttempt;
    data.settings.attemptsLeft = newAttemptsLeft;
    data.attemptsLeft = newAttemptsLeft;

    setLoading(true);

    try {
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
        .then((res) => res.json())
        .then((responseData) => {
          if (typeof onApiResponse === "function") {
            onApiResponse(responseData);
          }

          try {
            dispatch({ type: "SET_STATUS", payload: "PENDING" });
            dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
          } catch (err) {
            // Silent fail
          }

          setTimeout(() => {
            setLoading(true);
            setIsVerifying(false);
          }, 2000);

          setCurrentAttempt((prevAttempt) => prevAttempt + 1);
          waitingForConfirmationChangeRef.current = true;
          setResult(true);
        });
    } catch (error) {
      alert("Something Went Wrong! Please try again.");
      setIsVerifying(false);
    }
  };

  const handleDobChange = (dob) => {
    const formattedDob = `${dob.year}${dob.month}${dob.day}`;
    form.setFieldValue("dob", formattedDob);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  // Initial load effect
  useEffect(() => {
    setCurrentAttempt(0);
    setHasShownFormForCurrentAttempt(false);
    setAttemptsLeft(reAttempts);

    const errors = form.validate();
    if (!errors.hasErrors) {
      if (!data.settings) data.settings = {};
      data.settings.currentAttempt = 0;
      data.settings.totalAttempts = reAttempts + 1;
      data.attemptsLeft = reAttempts;
      setLoading(true);
      setResult(true);
    } else {
      setLoading(false);
      form.clearErrors();
    }
  }, [componentKey]);

  if (loading) {
    return <LoadingOverlay visible />;
  }

  const groupedFields = config.fields.reduce(
    (acc, field) => {
      if (field.group) {
        if (!acc.grouped[field.group]) {
          acc.grouped[field.group] = [];
        }
        acc.grouped[field.group].push(field);
      } else {
        acc.ungrouped.push(field);
      }
      return acc;
    },
    { grouped: {}, ungrouped: [] },
  );

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: "fadeIn 0.6s ease-out",
          minHeight: "100%",
        }}
      >
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box style={{ flex: 1, width: "100%" }}>
            <Stack spacing={8} mt={inputSize === "md" ? 5 : 5}>
              <Center>
                <ThemeIcon
                  variant="light"
                  c={
                    data.styles?.primaryColor
                      ? data.styles.primaryColor
                      : "teal"
                  }
                  size={inputSize === "md" ? 60 : 55}
                  radius="xl"
                >
                  {icon}
                </ThemeIcon>
              </Center>
              <Stack spacing={0} mt="sm" mb="lg">
                <Text fz={inputSize === "md" ? 24 : 20} fw={700}>
                  {title}
                </Text>
                <Text c="dimmed" fz={inputSize}>
                  {subtitle}
                </Text>
              </Stack>
            </Stack>
            <Stack spacing="md">
              {Object.keys(groupedFields.grouped).map((group) => (
                <MantineGroup key={group} spacing="md" noWrap grow>
                  {groupedFields.grouped[group].map((field) => (
                    <FormField
                      key={field.name}
                      field={field}
                      form={form}
                      settings={settings}
                      componentKey={componentKey}
                      showMoreFields={true}
                      onDobChange={handleDobChange}
                      onAddressChange={handleAddressChange}
                    />
                  ))}
                </MantineGroup>
              ))}

              <Stack spacing="md">
                {groupedFields.ungrouped.map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    form={form}
                    settings={settings}
                    componentKey={componentKey}
                    showMoreFields={true}
                    onDobChange={handleDobChange}
                    onAddressChange={handleAddressChange}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box mt="md" pb="10px" mb="80px" style={{ width: "100%" }}>
            <Button
              mt="xl"
              fullWidth
              size="lg"
              type="submit"
              disabled={isVerifying}
              loading={false}
              sx={{
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.01)",
                },
                '&[data-loading="true"]': {
                  color: buttonContentColor,
                },
              }}
            >
              {isVerifying ? (
                <MantineGroup spacing="xs" noWrap>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid transparent",
                      borderTop: `2px solid ${buttonContentColor}`,
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  <Text c="inherit">Sending Data</Text>
                </MantineGroup>
              ) : (
                buttonText
              )}
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default BasicDataCollectionForm;